import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/App";

const LINKS = [
  { id: "accueil", label: "Accueil" },
  { id: "babylon", label: "Babylon" },
  { id: "processus", label: "Comment ça fonctionne" },
  { id: "recherches", label: "Nos recherches" },
  { id: "faq", label: "FAQ" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 300 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/[0.06]" : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <button
            onClick={() => go("accueil")}
            className="flex flex-col items-start leading-none group"
            data-testid="nav-logo"
          >
            <img
              src="/images/logo.png"
              alt="BABYLON"
              className="h-6 sm:h-7 w-auto group-hover:opacity-80 transition-opacity duration-500"
            />
            <span className="font-mono text-[8px] tracking-[0.4em] text-gold/70 mt-1.5 uppercase">
              Conciergerie horlogère
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="text-[13px] text-stone2 hover:text-ivory transition-colors duration-300 tracking-wide"
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("demande")}
              className="ml-2 border border-gold/50 text-gold px-5 py-2.5 text-[12px] tracking-[0.2em] uppercase hover:bg-gold hover:text-obsidian transition-all duration-500"
              data-testid="nav-request-button"
            >
              Faire une demande
            </button>
          </nav>

          <button
            className="lg:hidden text-ivory p-2"
            onClick={() => setOpen(true)}
            aria-label="Ouvrir le menu"
            data-testid="nav-mobile-open"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-obsidian/98 backdrop-blur-2xl flex flex-col"
            data-testid="nav-mobile-menu"
          >
            <div className="h-[72px] px-5 flex items-center justify-between">
              <img src="/images/logo.png" alt="BABYLON" className="h-6 w-auto" />
              <button
                className="text-ivory p-2"
                onClick={() => setOpen(false)}
                aria-label="Fermer le menu"
                data-testid="nav-mobile-close"
              >
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-3xl text-ivory/90 py-3 border-b border-white/[0.06] hover:text-gold transition-colors"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                onClick={() => go("demande")}
                className="mt-8 bg-gold text-obsidian py-4 text-[13px] tracking-[0.25em] uppercase font-medium"
                data-testid="nav-mobile-request-button"
              >
                Faire une demande
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
